import { k8V2ContentfulMappings } from 'utils/productMaps'
import { getSlug } from 'utils/urlHelpers'

const DWB_SITE_PREFERENCE_NAME = 'dwbp_enabled'

const DWB_PROGRAMS = [
  'elementary',
  'middle-school',
  'high-school',
  'digital-well-being',
]

export const getTier2Slug = link => {
  const source = (link?.slug || link?.title || '')
    .toString()
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .trim()

  if (source.includes('6-8')) return 'grades-6-8'
  if (source.includes('3-5')) return 'grades-3-5'
  if (source.includes('k-2') || source.includes('k2')) return 'grades-k-2'

  // default: ensure plural 'grades-' prefix if starting with 'grade-'
  return source.replace(/^grade-/, 'grades-')
}

const getDefaultUnitSlugForCourse = course =>
  (
    k8V2ContentfulMappings?.find(
      contentfulCourse => contentfulCourse?.id === course?.entryId,
    ) || {}
  ).defaultUnit

export const getTier2PreviewPath = ({ program, course }) =>
  `/product/${program}/lessons/tier-2/preview/${getTier2Slug(
    course,
  )}/${getDefaultUnitSlugForCourse(course)}`

const isTrainingCourseLink = course => {
  const source = `${course?.slug || ''} ${course?.title || ''}`.toLowerCase()
  return source.includes('training')
}

const getTier1Slug = link => {
  const source = (link?.slug || link?.title || '')
    .toString()
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .trim()

  return source.replace(/\s+/g, '-')
}

// Resolves the URL segment between "/preview/" and "/<lesson>" for a Digital
// Well Being course: either "training" for training-style courses, or the
// course link slug from Contentful mappings. When a legacy caller omits the
// slug, derive the segment from the title instead.
const getDigitalWellBeingGradeOrTrainingSegment = course => {
  if (isTrainingCourseLink(course)) return 'training'

  const fromLink = course?.slug?.trim()
  if (fromLink) return fromLink

  return getTier1Slug({ title: course?.title }) || getSlug(course?.title || '')
}

// Default unit slug for DWB preview comes from course link `defaultLessonSlug`
// (sourced from productMaps per grade); fallback for legacy links without it.
const getDigitalWellBeingDefaultLessonSlug = course =>
  course?.defaultLessonSlug || 'unit-1'

export const getDigitalWellBeingPreviewPath = ({ course }) => {
  const segment = getDigitalWellBeingGradeOrTrainingSegment(course)
  const lesson = getDigitalWellBeingDefaultLessonSlug(course)

  return `/product/digital-well-being/lessons/preview/${segment}/${lesson}`
}

const getDigitalWellBeingCapabilityFromSite = ({ site, capabilityKey }) => {
  if (!site) return null

  const preferences = site?.preferences
  if (!Array.isArray(preferences)) return null

  const preference = preferences.find(
    sitePreference => sitePreference?.name === capabilityKey,
  )

  return Boolean(preference?.value)
}

export const isDigitalWellBeingCapabilityEnabledForProgram = ({
  site,
  program,
}) => {
  if (!DWB_PROGRAMS.includes(program)) return false

  const siteCapability = getDigitalWellBeingCapabilityFromSite({
    site,
    capabilityKey: DWB_SITE_PREFERENCE_NAME,
  })

  return siteCapability === null ? false : siteCapability
}

// Converts a tier 2 course title like "Grade 6-8" to display as "Grades 6-8".
// Accepts either a link-like object ({ title, slug }) or a plain string.
export const getTier2DisplayTitle = linkOrTitle => {
  const source = (typeof linkOrTitle === 'string'
    ? linkOrTitle
    : linkOrTitle?.title || linkOrTitle?.slug || ''
  )
    .toString()
    .trim()

  // If title already starts with "Grades", leave it as is
  if (/^\s*grades\b/i.test(source)) return source

  // Pluralize a leading "Grade" (case-insensitive), preserving following separator and content
  return source.replace(/^\s*grade(\s|-)/i, (_match, sep) => `Grades${sep}`)
}

export default {
  getTier2Slug,
  getTier2DisplayTitle,
  getTier2PreviewPath,
  getDigitalWellBeingPreviewPath,
  isDigitalWellBeingCapabilityEnabledForProgram,
}
